import { ScrollDispatcher } from '@angular/cdk/scrolling';
import { NgTemplateOutlet } from '@angular/common';
import {
  EmbeddedViewRef,
  forwardRef,
  OnDestroy,
  OnInit,
  SimpleChanges,
  TemplateRef,
  ChangeDetectorRef,
  Component,
  ComponentFactoryResolver,
  ElementRef,
  Injector,
  Input,
  Renderer2,
  SecurityContext,
  ViewChild,
  ViewEncapsulation,
  ViewContainerRef,
  output,
  inject,
} from '@angular/core';
import { outputToObservable } from '@angular/core/rxjs-interop';
import { FormControlDirective, NgControl } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { NavigationStart, Router } from '@angular/router';

import { deepEqual } from 'fast-equals';
import type { Observable } from 'rxjs';
import { BehaviorSubject, combineLatest, merge, of, Subject, timer } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  pluck,
  startWith,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';

import { I18nService } from '@core/i18n-legacy';
import {
  COMPONENT_TOOLTIP_RESIZE_PROVIDERS,
  ComponentTooltipResizeDispatcher,
} from '@core/product-updates-smart-ui';
import { RtTextInput } from '@core/user-interface-legacy';
import { encodeHTML, generateUuid } from '@core/utils-legacy';

import { RedactorAnalyticsService } from '../analytics/redactor.analytics';
import type { RedactorExtension } from './extensions/redactor-extension';
import { REDACTOR_EXTENSIONS } from './extensions/redactor-extension';
import { RedactorService } from './redactor.service';
import type { Redactor, RedactorConfig } from './redactor.types';
import { RedactorTranslations } from './translations';
import { WysiwygAdapterService } from '../wysiwyg-extensions/adapter/wysiwyg-adapter.service';
import { WysiwygData } from '../wysiwyg-extensions/adapter/wysiwyg-adapter.types';
import type { WysiwygLink } from '../wysiwyg-extensions/link/wysiwyg-link.types';
import {
  LinkActionType as WysiwygLinkActionType,
  GDPRLinkToken,
  WysiwygLinkType,
} from '../wysiwyg-extensions/link/wysiwyg-link.types';
import type { Placeholder } from '../wysiwyg-extensions/placeholders/placeholder';
import type { UploadPluginErrors } from './extensions/upload/upload';
import { ActionType as WysiwygPlaceholderActionType } from '../wysiwyg-extensions/placeholders/wysiwyg-placeholder/wysiwyg-placeholder.types';

// Redactor global
declare let $R: any;

/**
 @name Redactor
 @kind Forms

 @example
 <rt-redactor></rt-redactor>
 */
/* eslint-disable max-lines */

@Component({
  selector: 'rt-redactor',
  templateUrl: './redactor.component.html',
  styleUrls: ['./redactor.component.less'],
  // eslint-disable-next-line @angular-eslint/use-component-view-encapsulation
  encapsulation: ViewEncapsulation.None,
  host: {
    '[class.redactor--container]': 'true',
    '[class.read-only]': 'readOnly',
    '[class.no-bottom-radius]': 'borderRadius === "top"',
    '[class.no-top-radius]': 'borderRadius === "bottom"',
    '[class.with-radius]': 'borderRadius === "both"',
    '[class.without-radius]': '!borderRadius || borderRadius === "none"',
    '[class.with-default-styles]': 'visualLayerStyle === "default"',
    '[class.with-careers-styles]': 'visualLayerStyle === "careers"',
    '[class.with-email-signature-styles]': 'visualLayerStyle === "email-signature"',
    '[class.sticky-toolbar]': 'stickyToolbar',
    '[class.render-only]': 'renderOnly', // hide toolbar and borders, changes background color
  },
  providers: [
    {
      provide: RtTextInput,
      // eslint-disable-next-line @angular-eslint/no-forward-ref
      useExisting: forwardRef(() => RedactorComponent),
    },
    COMPONENT_TOOLTIP_RESIZE_PROVIDERS,
    RedactorAnalyticsService,
  ],
  imports: [NgTemplateOutlet],
})
export class RedactorComponent extends RtTextInput implements OnInit, OnDestroy {
  public static fixEmptyContent(html: string): string {
    // In some scenarios empty [contenteditable] can output <p><br /></p>,
    // which affects form validation.

    let htmlToReturn = html;
    const buggyStrings = ['<p><br></p>', '<p><br /></p>'];

    buggyStrings.forEach(buggyString => {
      if (html.startsWith(buggyString)) {
        htmlToReturn = html.slice(buggyString.length);
      }
    });

    return htmlToReturn;
  }

  public static applyContentFixes(html: string): string {
    return RedactorComponent.fixEmptyContent(html);
  }

  private readonly analytics = inject(RedactorAnalyticsService);
  private readonly renderer2 = inject(Renderer2);
  private readonly adapterSvc = inject(WysiwygAdapterService);
  private readonly componentFactoryResolver = inject(ComponentFactoryResolver);
  private readonly injector = inject(Injector);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly extensions = inject(REDACTOR_EXTENSIONS) ?? [];
  private readonly visualLayerComponentsData = inject(WysiwygData);
  private readonly redactorService = inject(RedactorService);
  private readonly i18nService = inject(I18nService);
  private readonly scrollDispatcher = inject(ScrollDispatcher);
  private readonly componentTooltipResize = inject(ComponentTooltipResizeDispatcher);
  private readonly redactorTranslations = inject(RedactorTranslations);
  private readonly router = inject(Router);
  private readonly vcRef = inject(ViewContainerRef);
  private readonly CSS_SCOPING_SELECTOR =
    'rt-redactor .redactor-box.redactor-styles-on .redactor-styles.redactor-in';

  protected readonly ngControl?: NgControl;
  public readonly elRef = inject(ElementRef);
  public readonly cdRef: ChangeDetectorRef;

  @Input() public set uuid(id: string) {
    if (id !== this.uuid) {
      const prev = this.uuid;
      this._uuid = id || generateUuid();
      this.onContextChange(prev, this.uuid);
    }
  }
  public get uuid(): string {
    return this._uuid;
  }

  public initialValue: string;
  public disabled$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  @Input() public config: RedactorConfig = this.redactorService.defaultConfig;
  @Input() public placeholder: string;
  @Input() public autoFocus: boolean;
  @Input() public set disabled(value: boolean) {
    this.disabled$?.next(value);
  }
  public get disabled(): boolean {
    return this.disabled$.getValue();
  }
  @Input() public buttonTemplate?: TemplateRef<any>;
  @Input() public buttonContext: any;
  @Input() public footerContext: any;
  @Input() public footerTemplate: TemplateRef<any>;
  @Input() public noBottomRadius: boolean = false;
  @Input() public placeholders: Placeholder[] = [];
  @Input() public borderRadius: 'both' | 'top' | 'bottom' | 'none' = 'none';
  @Input() public visualLayerStyle: 'default' | 'careers' | 'email-signature' = 'default';
  @Input() public stickyToolbar: boolean = false;
  @Input() public stickyToolbarOffset: number = 0;
  @Input() public renderOnly: boolean = false; //

  // Security: If true, we'll bypass security sanitization here,
  // and value will be injected directly in a `innerHTML` context
  // (remember: it's a serious XSS vulnerability then, if it's not properly sanitized somehow else)
  @Input() public bypassSecurityTrustHtml: boolean = true;

  public syncBefore = output<void>();
  public uploadError = output<UploadPluginErrors>();

  @ViewChild('textarea', { static: true }) private textarea: ElementRef<HTMLTextAreaElement>;

  private _uuid: string;
  private redactor: Redactor;
  private presentationElement: HTMLDivElement | undefined;
  private presentationElementSize: { height: number; width: number } | undefined;
  private controlInitialized = false;
  private changed$ = new Subject<string>();
  private redactorApiEvents$ = new Subject<string>();
  private redactorDestroy$ = new Subject<void>();
  private contextClose$ = new Subject<void>();
  private contextDestroy$: Observable<any> = merge(
    this.ngOnDestroy$.asObservable(),
    this.contextClose$.asObservable(),
  );

  private buttonViewDestroy$ = new Subject<void>();
  private buttonView: EmbeddedViewRef<any>;

  constructor() {
    const cdRef = inject(ChangeDetectorRef);
    const ngControl = inject(NgControl, { optional: true, self: true }) ?? undefined;

    super(cdRef, ngControl);

    this.cdRef = cdRef;
    this.ngControl = ngControl;
  }

  public onContextChange(prevId: string, nextId: string): void {
    if (this.redactor) {
      this.controlInitialized = false;
      this.renewContextDataSubscription(prevId, nextId);
      this.focus.emit(undefined);
    }
  }

  public ngOnInit(): void {
    this.scopeExternalStyles();
    this.setInitialValue();

    this.redactorApiEvents$
      .pipe(
        filter(event => event === 'plugin.fullscreen.toggle'),
        tap(() => this.analytics.fullscreenToggle()),
        map(() => this.redactor.plugin.fullscreen.isOpen),
        takeUntil(this.ngOnDestroy$),
      )
      .subscribe((isFullScreen: boolean) =>
        isFullScreen
          ? this.applyToolbarOffset(0)
          : this.applyToolbarOffset(this.stickyToolbarOffset),
      );

    this.redactorApiEvents$
      .pipe(
        filter(event => event === 'module.source.toggle'),
        takeUntil(this.ngOnDestroy$),
      )
      .subscribe(() => this.analytics.htmlToggle());

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationStart),
        takeUntil(this.ngOnDestroy$),
      )
      .subscribe(() => this.closeFullscreen());

    this.redactorDestroy$.pipe(takeUntil(this.ngOnDestroy$)).subscribe(() => {
      this.closeFullscreen();
      $R(this.getNativeInputElement(), 'destroy');
    });

    this.scrollDispatcher
      .scrolled(0)
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(() => {
        const resizer = this.redactor?.module?.image?.resizer;
        if (resizer) {
          resizer._setResizerPosition();
        }
      });
  }

  public ngAfterViewInit(): void {
    this.createRedactor();
    this.watchDisabledStatusChanges();
  }

  public ngOnDestroy(): void {
    this.redactorDestroy$.next();
    this.redactorDestroy$.complete();
    super.ngOnDestroy();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if ('value' in changes) {
      if (
        !changes.value.firstChange &&
        changes.value.currentValue !== this.redactor.api('source.getCode')
      ) {
        this.writeValue(changes.value.currentValue);
      }
    }

    if ('config' in changes && !changes.config.firstChange) {
      if (this.redactor && !deepEqual(changes.config.currentValue, changes.config.previousValue)) {
        this.redactorDestroy$.next();
        this.createRedactor();
      }
    }

    if (this.redactor && 'rt-placeholder' in this.redactor.plugin) {
      if ('placeholders' in changes && !changes.placeholders.firstChange) {
        this.updatePlaceholders(this.placeholders);
      }
    }

    if (this.buttonView && 'buttonContext' in changes && !changes.buttonContext.firstChange) {
      this.buttonView.context = changes.buttonContext.currentValue;
      this.buttonView.detectChanges();
    }
  }

  public getNativeInputElement(): HTMLTextAreaElement {
    return this.textarea.nativeElement;
  }

  public updatePlaceholders(placeholders: Placeholder[]): void {
    this.redactor.api('plugin.rt-placeholder.updatePlaceholdersData', placeholders || []);
  }

  public redactorInsertHtml(html: string): void {
    this.redactor.api('editor.startFocus');
    this.redactor.api('selection.restore');
    this.redactor.api('insertion.insertRaw', html);
  }

  public insertAtCaret(
    text: string | WysiwygLink,
    type: 'html' | 'placeholder' | 'link' | 'plaintext' = 'html',
  ): void {
    switch (type) {
      case 'link':
        this.redactor.api('plugin.rt-link.insert', text);
        break;

      case 'placeholder':
        if (this.ngControl?.disabled) return;

        const knownPlaceholder =
          this.placeholders.length && this.placeholders.find(p => p.id === text);

        if (knownPlaceholder) {
          this.redactor.api(
            'plugin.rt-placeholder.insertPlaceholder',
            this.placeholders.find(p => p.id === text),
          );
        } else {
          this.redactor.api('insertion.insertText', text);
        }
        break;

      case 'html':
        this.redactorInsertHtml(text.toString());
        break;

      default:
        return;
    }
  }

  public saveSelection(): void {
    this.redactor.api('selection.save');
  }

  public restoreSelection(): void {
    this.redactor.api('selection.restore');
  }

  public writeValue(value: string): void {
    // @ts-ignore
    if (this.cdRef['destroyed']) {
      return;
    }

    this.controlInitialized = !!this.ngControl?.control?.dirty;
    this.setInitialValue(value || '');
    if (this.redactor) {
      this.redactor.api('source.setCode', this.initialValue);
    }

    super.writeValue(this.initialValue);

    if (!this.controlInitialized) {
      this.controlInitialized = true;
      this.changed$.pipe(take(1), takeUntil(merge(this.ngOnDestroy$, timer(300)))).subscribe(() => {
        const control = this.ngControl?.control;
        if (control && control.dirty) {
          control.markAsPristine();
          control.updateValueAndValidity({ emitEvent: true });
        }
      });
    }
  }

  public hasLinkInserted$(types: WysiwygLinkType[]): Observable<Array<string | boolean>> {
    const gdprConsent = types.includes(WysiwygLinkType.GdprConsentRequest);
    const gdprRemove = types.includes(WysiwygLinkType.GdprRemove);
    const gdprChange = types.includes(WysiwygLinkType.GdprChange);
    const hasGdpr = gdprConsent || gdprRemove || gdprChange;

    return combineLatest([
      this.adapterSvc.components$.pipe(
        switchMap(componentsMap => {
          const entries = [...componentsMap.entries()];
          return entries.length
            ? combineLatest<Array<string | null>>(
                entries.map(([key, { instance }]) =>
                  instance.state$.pipe(
                    switchMap(state =>
                      types.includes((<WysiwygLink>state)?.type!) ? of(key) : of(null),
                    ),
                    take(1),
                  ),
                ),
              )
            : of<string[]>([]);
        }),
      ),
      ...(hasGdpr
        ? [
            outputToObservable(this.valueChange).pipe(
              startWith(this.value),
              map(value => {
                if (!value) return false;

                return Boolean(
                  (gdprRemove && value.includes(GDPRLinkToken.GdprRemove)) ||
                    (gdprChange && value.includes(GDPRLinkToken.GdprChange)) ||
                    (gdprConsent && value.match(/<a.*?href=".*?\/v\/gdpr\/consent\/.*?>/gm)),
                );
              }),
            ),
          ]
        : []),
    ]).pipe(
      map(([linkKeys, gdpr]: [string[] | null[], boolean]) => [
        ...(<string[]>linkKeys).filter(key => key !== null),
        ...(gdpr ? [true] : []),
      ]),
    );
  }

  public insertWidget(html: string): void {
    const parsedEl = encodeHTML(html);
    if (parsedEl !== html) console.warn('Redactor Widget: insertWidget input HTML is not valid');

    if (!this.redactor.plugin.widget) {
      console.warn('Redactor Widget: plugin is not registered!');
      return;
    }

    this.redactor.plugin.widget._insert({ widget: parsedEl });
  }

  private handleUploadErrors(uploadPluginErrors: UploadPluginErrors): void {
    if ('rt-upload' in this.redactor.plugin) {
      this.uploadError.emit(uploadPluginErrors);
    } else {
      console.warn('Redactor upload plugin has not been installed.');
    }
  }

  private sanitizeValue(html: string): string {
    const strippedHtml = html ? html.replace(/ data-children-count="\d*"/g, '') : html;

    return this.bypassSecurityTrustHtml
      ? strippedHtml
      : this.sanitizer.sanitize(SecurityContext.HTML, strippedHtml)!;
  }

  private handleRedactorSyncBefore(html: string): string {
    return RedactorComponent.applyContentFixes(this.sanitizeValue(html));
  }

  private injectBreakLineIntoEmptyTags(text: string): string {
    return text.replace(/<([^/>][^>]*)([^>]*?)><\/\1>/gi, '<$1$2><br></$1>');
  }

  private trimDataAtributes(text: string): string {
    return text.replace(/\s+data-\w+(-\w+)*=".*?"/g, '');
  }

  private buildConfig(): RedactorConfig {
    const baseConfig = {
      ...(this.config || {}),
      focus: this.autoFocus,
      placeholder: this.placeholder,
      callbacks: {
        ...((this.config && this.config.callbacks) || {}),
        started: () => {
          this.presentationElement = this.elRef.nativeElement.querySelector('[role=presentation]');
        },
        changed: (nextHtml: string) => {
          const sanitizedNextHtml = this.injectBreakLineIntoEmptyTags(this.sanitizeValue(nextHtml));
          // some extensions add data attributes (e.g. dashlane frame id) to the html,
          // so we need to cut them out them before comparing to avoid unnecessary change emission
          const trimmedNextHtml = this.trimDataAtributes(sanitizedNextHtml);
          const trimmedValue = this.trimDataAtributes(this.value || '');

          if (trimmedNextHtml !== trimmedValue) {
            this.handleChange(sanitizedNextHtml);
          }
          this.changed$.next(sanitizedNextHtml);
        },
        syncBefore: (html: string) => {
          this.syncBefore.emit();
          this.checkSizeChange();
          this.handleRedactorSyncBefore(html);
        },
      },
      lang: this.i18nService.locale,
    };

    return this.extensions.reduce(
      (config: RedactorConfig, extension: RedactorExtension) =>
        extension.canInstall(config) ? extension.install(config) : config,
      baseConfig,
    );
  }

  private checkSizeChange() {
    if (!this.presentationElement) return;
    const currentSize = {
      height: this.presentationElement.offsetHeight,
      width: this.presentationElement.offsetWidth,
    };

    if (!this.presentationElementSize) {
      this.presentationElementSize = currentSize;
      return;
    }

    const isSameSize =
      currentSize.height === this.presentationElementSize.height &&
      currentSize.width === this.presentationElementSize.width;

    this.presentationElementSize = currentSize;
    if (!isSameSize) {
      this.componentTooltipResize.next();
    }
  }

  private registerTranslations(): void {
    const { locale, translations } = this.redactorTranslations;
    $R.lang[locale] = translations;
  }

  private createRedactor(): string {
    this.registerTranslations();

    const config = this.buildConfig();

    if (!this.uuid) {
      this.uuid = generateUuid();
    }

    this.redactor = $R(this.getNativeInputElement(), config);

    const redactorApi = this.redactor.api.bind(this.redactor);
    this.redactor.api = (...args: [string, ...any[]]) => {
      const result = redactorApi(...args);
      this.redactorApiEvents$.next(args[0]);
      return result;
    };

    this.redactor.on('focus', (e: UIEvent) => this.focus.emit(e));
    this.redactor.on('blur', (e: UIEvent) => this.blur.emit(e));

    this.redactor.on(
      'ngComponentsDiff',
      (
        toAdd: string[],
        toRemove: string[],
        toTransfer: string[],
        data: Map<string, { [key: string]: any }>,
      ) => {
        if (toAdd.length) this.adapterSvc.ngHostAdd(this.uuid, toAdd, this.injector, data);
        if (toRemove.length) this.adapterSvc.ngHostRemove(toRemove, this.uuid);
        if (toTransfer.length)
          this.adapterSvc.ngHostTransfer(this.uuid, toTransfer, this.injector, data);
      },
    );

    this.redactor.on('ngDataChange', (id: string, data: { [key: string]: any }) =>
      this.adapterSvc.ngHostUpdate(id, data),
    );

    this.redactor.on('uploadPluginError', (uploadErrors: UploadPluginErrors) => {
      this.handleUploadErrors(uploadErrors);
    });

    this.redactor.on('uploadPluginDone', (type: 'image' | 'other') => {
      this.analytics.fileUpload(type);
    });

    if ('rt-placeholder' in this.redactor.plugin) {
      this.updatePlaceholders(this.placeholders);
    }

    this.subscribeContextData(this.uuid, this.contextDestroy$);
    this.redactor.api('source.setCode', this.initialValue);
    this.redactor.broadcast('observe');

    this.redactor.on('stop', () => {
      this.adapterSvc.flush(this.uuid);
      this.contextClose$.next();
      this.contextClose$.complete();
    });

    this.applyToolbarPatches();

    return this.uuid;
  }

  private applyToolbarPatches(): void {
    if (this.config && this.config.toolbar === false) return;

    const toolbar: HTMLElement = this.redactor.toolbar.getElement().get();
    const floatedContainer = document.createElement('div');
    const defaultContainer = document.createElement('div');

    floatedContainer.classList.add('floated-container');
    while (toolbar.children.length) {
      const child = toolbar.children[0];
      defaultContainer.appendChild(child);
    }
    toolbar.prepend(floatedContainer, defaultContainer);

    if (this.buttonTemplate) {
      this.buttonViewDestroy$.next();
      this.buttonView = this.vcRef.createEmbeddedView(this.buttonTemplate, this.buttonContext, {
        injector: this.injector,
      });
      this.buttonView.rootNodes.forEach(node => floatedContainer.appendChild(node));
      this.redactorDestroy$
        .pipe(take(1), takeUntil(merge(this.ngOnDestroy$, this.buttonViewDestroy$)))
        .subscribe(() => this.buttonView.destroy());
    }

    this.sortToolbar(defaultContainer, floatedContainer);
    this.applyToolbarOffset(this.stickyToolbarOffset);
  }

  private sortElements(elements: HTMLElement[], order: string[]): HTMLElement[] {
    return [...elements]
      .map(element => ({
        index: order.findIndex(className => element.classList.contains(className)),
        element,
      }))
      .filter(({ index }) => index !== -1)
      .sort(({ index: aIndex }, { index: bIndex }) => (aIndex > bIndex ? 1 : -1))
      .map(({ element }) => element);
  }

  private sortToolbar(defaultContainer: HTMLElement, floatedContainer: HTMLElement): void {
    const elements = <HTMLElement[]>Array.from(defaultContainer.children);
    while (defaultContainer.children.length) {
      defaultContainer.children[0].remove();
    }
    const toolbarOrder = [
      're-bold',
      're-italic',
      're-underline',
      're-format',
      're-fontsize',
      're-fontfamily',
      're-fontcolor',
      're-ul',
      're-ol',
      're-align-left',
      're-align-center',
      're-align-right',
      're-alignment',
      're-link',
      're-image',
      're-video',
      're-file',
      're-undo',
      're-redo',
      're-html',
    ];
    const floatedOrder = ['re-fullscreen'];
    const toolbarElements = this.sortElements(elements, toolbarOrder);
    const floatedElements = this.sortElements(elements, floatedOrder);

    toolbarElements.forEach(element => defaultContainer.appendChild(element));
    floatedElements.forEach(element => floatedContainer.appendChild(element));
  }

  private applyToolbarOffset(offset: number): void {
    const toolbarContainer: HTMLElement = this.redactor.toolbar.getWrapper().get();
    this.renderer2.setStyle(toolbarContainer, 'top', `${offset}px`);
  }

  private subscribeContextData(contextId: string, destroy$: Observable<any>): void {
    if ('rt-link' in this.redactor.plugin) {
      this.visualLayerComponentsData
        .stream()
        .pipe(takeUntil(destroy$), pluck('links'), distinctUntilChanged(deepEqual))
        .subscribe(links => {
          this.redactor.api('plugin.rt-link.updateLinksData', links);
        });
    }

    this.adapterSvc
      .getContextOutput(contextId)
      .pipe(
        takeUntil(destroy$),
        map(action => {
          const el = document.getElementById(action.trackingProps!.hostId)!.parentElement;
          const cmp = this.redactor.component.build(el);
          return [action, cmp];
        }),
      )

      .subscribe(([action, redactorCmp]) => {
        switch (action.type) {
          case WysiwygPlaceholderActionType.PlaceholderChange:
            redactorCmp.pushDataToAngularComponent(
              {
                active: action.payload,
                placeholders: this.placeholders,
              },
              action.payload.value,
            );
            break;

          case WysiwygLinkActionType.LinkDelete:
          case WysiwygPlaceholderActionType.PlaceholderDelete:
            this.redactor.component.remove(redactorCmp);
            break;

          case WysiwygPlaceholderActionType.PlaceholderDropdownDelete:
            this.redactor.component.remove(redactorCmp);
            break;

          case WysiwygPlaceholderActionType.PlaceholderInsertFromDropdown:
            this.redactor.component.remove(redactorCmp);
            this.redactor.api(
              'plugin.rt-placeholder.insertPlaceholderFromDropdown',
              action.payload,
            );
            break;

          case WysiwygLinkActionType.LinkEdit:
            redactorCmp.pushDataToAngularComponent(
              action.payload,
              `<a href="${action.payload.url}" target="_blank">${action.payload.label}</a>`,
            );

            break;

          default:
            return;
        }
      });
  }

  private renewContextDataSubscription(prevUuid: string, nextUuid: string): void {
    this.adapterSvc.flush(prevUuid);
    this.contextClose$.next();
    this.subscribeContextData(nextUuid, this.contextDestroy$);
  }

  private setInitialValue(value: string = this.getFormControlValue() || this.value || ''): string {
    this.initialValue = this.sanitizeValue(value);
    return this.initialValue;
  }

  private getFormControlValue(): string | null {
    if (!this.ngControl) return null;
    if (this.ngControl instanceof FormControlDirective) return this.ngControl.form.value;

    return this.ngControl.value;
  }

  private watchDisabledStatusChanges(): void {
    this.disabled$
      .pipe(
        map(disabled => (disabled ? 'module.editor.disable' : 'module.editor.enable')),
        distinctUntilChanged(),
        debounceTime(50),
        takeUntil(this.ngOnDestroy$),
        filter(() => !!this.redactor),
      )
      .subscribe((action: string) => {
        this.redactor.api(action);
      });
  }

  private closeFullscreen(): void {
    const { fullscreen } = this.redactor.plugin;
    if (fullscreen && fullscreen.isOpen) {
      fullscreen.close();
    }
  }

  private isGroupingRule(cssRule: CSSGroupingRule | CSSStyleRule): cssRule is CSSGroupingRule {
    return !!(<CSSGroupingRule>cssRule).cssRules?.length;
  }

  private scopeCssRules(cssRules?: CSSRuleList) {
    if (!cssRules) return;

    const rulesArray = Array.from(cssRules);
    rulesArray.forEach((cssRule: CSSGroupingRule | CSSStyleRule) => {
      const asStyleRule = <CSSStyleRule>cssRule;
      if (this.isGroupingRule(cssRule)) {
        this.scopeCssRules(cssRule.cssRules);
      } else if (
        asStyleRule.selectorText &&
        !asStyleRule.selectorText.startsWith(this.CSS_SCOPING_SELECTOR)
      ) {
        asStyleRule.selectorText = `${this.CSS_SCOPING_SELECTOR} ${asStyleRule.selectorText}`;
      }
    });
  }

  private scopeExternalStyles() {
    const mutationObserver = new MutationObserver(mutationRecord => {
      const styleNodes = <HTMLStyleElement[]>(
        mutationRecord.flatMap(({ addedNodes }) =>
          Array.from(addedNodes).filter(({ nodeName }) => 'STYLE' === nodeName),
        )
      );
      styleNodes.forEach(styleNode => this.scopeCssRules(styleNode.sheet?.cssRules));
    });

    mutationObserver.observe(this.elRef.nativeElement, {
      childList: true,
      subtree: true,
    });

    this.ngOnDestroy$.subscribe({
      complete: () => mutationObserver.disconnect(),
    });
  }
}
